
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "@/hooks/useNotify";
import { supabase } from "@/integrations/supabase/client";
import { InvitationData, CompanyData } from "../types";

export const useProfileSubmission = (
  invitation: InvitationData | null,
  company: CompanyData | null
) => {
  const [isLoading, setIsLoading] = useState(false);
  const notify = useNotify();
  const navigate = useNavigate();

  const handleProfileSubmit = async (profileData: {
    firstName: string;
    lastName: string;
    companyRole: string;
    personalPhoneNumber: string;
    profilePhotoUrl: string | null;
  }) => {
    if (!invitation || !company) {
      console.error("Missing invitation or company data");
      return;
    }

    try {
      setIsLoading(true);
      console.log("[ProfileSubmission] Starting profile submission process");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("No authenticated user found");
      }

      console.log("[ProfileSubmission] Joining company via atomic RPC");

      // Atomic join: membership + profile link/completion + invitation-accept
      // all succeed or fail together (join_company_with_member RPC). Company is
      // derived server-side from the invitation; client sends no company_id.
      const { data: joinResult, error: joinError } = await (supabase as any)
        .rpc("join_company_with_member", {
          p_invitation_id: invitation.id,
          p_first_name: profileData.firstName,
          p_last_name: profileData.lastName,
          p_company_position: profileData.companyRole,
          p_personal_phone_number: profileData.personalPhoneNumber,
          p_profile_photo_url: profileData.profilePhotoUrl,
        });

      if (joinError) {
        console.error("Join RPC error:", joinError);
        throw joinError;
      }

      if (!joinResult?.success) {
        throw new Error(joinResult?.error || "Failed to join company");
      }

      console.log("[ProfileSubmission] All operations completed successfully");
      notify.success(`You've successfully joined ${company.name}. Redirecting to dashboard...`);

      setTimeout(() => {
        console.log("[ProfileSubmission] Redirecting to dashboard with page reload");
        window.location.href = "/dashboard";
      }, 1500);

    } catch (error: any) {
      console.error("Profile submission error:", error);
      notify.error(error.message || "An error occurred while setting up your profile");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handleProfileSubmit
  };
};
